import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { useMemberstack } from "@/lib/memberstack";

/**
 * Login + password-reset flow.
 * Two stages on one page:
 *   stage="login"  — email + password sign-in (with "Forgot password?" link)
 *   stage="verify" — enter the 6-digit reset code emailed to you + a new password
 */
export default function Login() {
  const navigate = useNavigate();
  const { memberstack, isAuthenticated, loading: authLoading } = useMemberstack();

  const [stage, setStage] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Autofill rescue: some browsers fill inputs but don't fire React's onChange.
  useEffect(() => {
    const t = setTimeout(() => {
      const el = document.getElementById("login-email");
      const pw = document.getElementById("login-password");
      if (el?.value && !email) setEmail(el.value);
      if (pw?.value && !password) setPassword(pw.value);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!memberstack || submitting) return;
    const trimmedEmail = email.trim();
    if (trimmedEmail.length < 5 || password.length < 1) {
      toast.error("Need both fields", {
        description: "Type your email and password, then sign in.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await memberstack.loginMemberEmailPassword({
        email: trimmedEmail,
        password,
      });
      if (res?.errors?.length) {
        throw new Error(res.errors[0].message || "Login failed");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.message || "Couldn't sign you in. Check your details.";
      toast.error("Sign-in issue", { description: msg });
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!memberstack || sendingReset) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Need your email first", {
        description: "Type your email, then tap reset.",
      });
      return;
    }
    setSendingReset(true);
    try {
      await memberstack.sendMemberResetPasswordEmail({ email: trimmed });
    } catch (err) {
      // Memberstack throws a privacy-friendly response for unknown emails:
      // "If this email exists, you'll receive an email with the reset instructions."
      // That message is by design — we should treat it the same as success
      // so we don't leak which emails are on file.
      const msg = (err?.message || "").toLowerCase();
      const isPrivacyMessage =
        msg.includes("if this email exists") ||
        msg.includes("you'll receive an email");
      if (!isPrivacyMessage) {
        toast.error("Reset issue", {
          description: err?.message || "Couldn't send the reset email.",
        });
        setSendingReset(false);
        return;
      }
    }
    toast.success("Reset code sent", {
      description: `If ${trimmed} is on file, the 6-digit code is in your inbox.`,
    });
    setStage("verify");
    setSendingReset(false);
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    if (!memberstack || resettingPw) return;
    const code = resetCode.trim();
    if (code.length < 6) {
      toast.error("Need the 6-digit code", {
        description: "Check the email we sent and paste the code here.",
      });
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password too short", {
        description: "Use at least 8 characters.",
      });
      return;
    }
    setResettingPw(true);
    try {
      // Memberstack DOM SDK uses `token` for the 6-digit code from the email.
      const res = await memberstack.resetMemberPassword({
        token: code,
        newPassword,
      });
      if (res?.errors?.length) {
        throw new Error(res.errors[0].message || "Reset failed");
      }
      toast.success("Password updated", { description: "Signing you in…" });

      // Auto-login with the new password so they don't have to re-enter
      try {
        await memberstack.loginMemberEmailPassword({
          email: email.trim(),
          password: newPassword,
        });
        navigate("/dashboard", { replace: true });
      } catch {
        // Fall back to manual sign-in if auto-login fails
        setStage("login");
        setPassword("");
        toast("Password set — please sign in.", {
          description: "Your new password is ready.",
        });
        setResettingPw(false);
      }
    } catch (err) {
      const msg =
        err?.message ||
        "Couldn't reset your password. Double-check the code (it expires after 1 hour).";
      toast.error("Reset issue", { description: msg });
      setResettingPw(false);
    }
  };

  const backToLogin = () => {
    setStage("login");
    setResetCode("");
    setNewPassword("");
  };

  return (
    <div className="App" data-testid="login-page">
      <SiteHeader
        onLogoClick={() =>
          window.location.assign("https://www.wearetheweave.co.uk")
        }
      />

      <main className="max-w-md mx-auto px-6 pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p
            className="text-[11px] uppercase tracking-[0.22em] font-medium"
            style={{ color: "#3c8386" }}
            data-testid="login-eyebrow"
          >
            The Weave's 1% Club
          </p>
          <h1
            className="mt-4 text-3xl sm:text-4xl font-light text-slate-800 tracking-tight leading-tight"
            data-testid="login-headline"
          >
            {stage === "login" ? "Welcome back." : "Set a new password."}
          </h1>
          <p className="mt-3 text-base text-slate-500">
            {stage === "login"
              ? "Sign in to your founding member space."
              : `We've sent a 6-digit code to ${email.trim() || "your inbox"}.`}
          </p>
        </motion.div>

        {stage === "login" && (
          <form
            onSubmit={handleLogin}
            className="mt-10 space-y-5"
            data-testid="login-form"
          >
            <div>
              <label
                className="block text-sm text-slate-500 mb-2"
                htmlFor="login-email"
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                className="weave-input"
                placeholder="you@yourwork.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={submitting}
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  className="block text-sm text-slate-500"
                  htmlFor="login-password"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  className="text-xs text-slate-500 hover:text-[#3c8386] transition-colors"
                  disabled={sendingReset || submitting}
                  data-testid="login-reset-btn"
                >
                  {sendingReset ? "Sending…" : "Forgot password?"}
                </button>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  className="weave-input pr-12"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={submitting}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="weave-pw-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  disabled={submitting}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="weave-btn-primary w-full"
              disabled={submitting}
              data-testid="login-submit"
            >
              {submitting ? (
                <>
                  <span className="weave-spinner" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        )}

        {stage === "verify" && (
          <form
            onSubmit={handleVerifyReset}
            className="mt-10 space-y-5"
            data-testid="reset-verify-form"
          >
            <div>
              <label
                className="block text-sm text-slate-500 mb-2"
                htmlFor="reset-code"
              >
                6-digit code
              </label>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                className="weave-input tracking-[0.4em] text-center text-lg"
                placeholder="123456"
                value={resetCode}
                onChange={(e) =>
                  setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                disabled={resettingPw}
                data-testid="reset-code"
                autoFocus
              />
              <p className="mt-2 text-xs text-slate-400">
                The code in your email expires after 1 hour.
              </p>
            </div>

            <div>
              <label
                className="block text-sm text-slate-500 mb-2"
                htmlFor="reset-new-password"
              >
                New password
              </label>
              <div className="relative">
                <input
                  id="reset-new-password"
                  type={showNewPassword ? "text" : "password"}
                  className="weave-input pr-12"
                  placeholder="at least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={resettingPw}
                  minLength={8}
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="weave-pw-toggle"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  aria-pressed={showNewPassword}
                  tabIndex={-1}
                  disabled={resettingPw}
                  data-testid="reset-new-password-toggle"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="weave-btn-primary w-full"
              disabled={resettingPw}
              data-testid="reset-submit"
            >
              {resettingPw ? (
                <>
                  <span className="weave-spinner" />
                  Updating password…
                </>
              ) : (
                "Set new password & sign in"
              )}
            </button>

            <div className="flex items-center justify-between gap-3 pt-2 text-sm">
              <button
                type="button"
                onClick={backToLogin}
                className="inline-flex items-center gap-1 text-slate-500 hover:text-[#3c8386] transition-colors"
                disabled={resettingPw}
                data-testid="reset-back-link"
              >
                <ArrowLeft size={14} />
                Back to sign in
              </button>
              <button
                type="button"
                onClick={handlePasswordReset}
                className="text-slate-500 hover:text-[#3c8386] transition-colors"
                disabled={sendingReset || resettingPw}
                data-testid="reset-resend-btn"
              >
                {sendingReset ? "Resending…" : "Resend code"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-10 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
          Not a member yet?{" "}
          <Link
            to="/join"
            className="font-medium text-slate-700 hover:text-[#3c8386] transition-colors"
            data-testid="login-join-link"
          >
            Join the 1% Club
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
